import type { KpiCardSpec } from '../kpis/card-spec';
import { ACTIVE_USERS_SCALAR_CARD } from '../kpis/active-users-scalar-card';
import { COUPONS_REDEEMED_SCALAR_CARD } from '../kpis/coupons-redeemed-scalar-card';
import { ALL_TIME_DOWNLOADS_SCALAR_CARD } from '../kpis/all-time-downloads-scalar-card';
import { withDashboardLookAll } from '../kpis/with-dashboard-look';

/**
 * Overview seed cards, grouped by dashboard section.
 *
 * Cards follow the page-level Period + Region filter; All-time downloads
 * departs from them endpoint-side and says so in its own description.
 * Where a real standardized source exists it is used directly; data points we
 * don't have a source for yet fall back to the `placeholder` source, which
 * renders a grayed "sample" card so the layout is complete before the data
 * plumbing lands.
 */

/**
 * Featured hero KPIs: a single number or rate with a period-over-period delta.
 *
 * All-time downloads opens the row as a single figure. The daily two-line
 * chart that used to sit here moved to the Acquisition page, which is where
 * downloads are examined; this keeps the at-a-glance number the Overview is
 * for. It is the one card in the row the page filters never move (see
 * `ALL_TIME_DOWNLOADS_SCALAR_CARD`).
 */
export const FEATURED_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        ALL_TIME_DOWNLOADS_SCALAR_CARD,
        ACTIVE_USERS_SCALAR_CARD,
        {
            title: 'Age verification',
            source: 'age-gating',
            form: 'number',
            metric: 'ageGatedUsers',
            size: 'sm',
            description:
                'Users who went through age verification in the selected period.',
        },
        COUPONS_REDEEMED_SCALAR_CARD,
    ],
    'blue'
);

/** Key numbers: exploded charts worth seeing without a click. */
export const KEY_NUMBER_CARDS: KpiCardSpec[] = withDashboardLookAll(
    [
        {
            // Coverage snapshot from the onboarding-state endpoint: of the
            // accounts registered in the selected period, the current share
            // sitting in each of six states, as six independent bars. Not an
            // ordered funnel: `complete` can legitimately exceed `segmentation`
            // (a skipped optional step still finishes onboarding), so the bars
            // are read as a coverage picture today, not a drop-off sequence.
            // National only (no region pin needed: the source's own
            // `regionGrain: 'national'` makes `createEnvelopeSource` drop a
            // selected region before the request goes out) and no coupon
            // selection; `bars` never compares (see `resolveCardCompare`), so
            // this card has no period-over-period delta. The backend emits no
            // warning for it. `barBaseInHeader`: every bar shares one denominator
            // (`registered`, via the source's `barsBaseMetricId`), so the base
            // shows once in the title (e.g. "Onboarding state coverage (1,240)")
            // instead of repeating "NN% (1,240)" on all six rows (house pattern,
            // see `ENGAGEMENT_DRIVER_CARDS` in rpp-dashboard/cards.ts).
            title: 'Onboarding state coverage',
            source: 'onboarding-state',
            form: 'bars',
            size: 'md',
            description:
                'The current state of the accounts registered in the selected period: each bar is the share of those registered accounts sitting in that state today. The bars are independent, not an ordered progression, so Complete can exceed Segmentation when a skipped optional step still lets an account finish onboarding. Follows the page Period filter; Region is accepted and ignored (national only, not broken down by province).',
            barBaseInHeader: true,
        },
        {
            // Replaced the per-coupon "Coupon activated" split: one coupon at a
            // time answered less than the period's ranking does at a glance. Same
            // rows the Acquisition page shows as bars, raised to ten via the card
            // `limit`; a truncated top-N, so the slices share out the top ten only.
            title: 'Top coupons',
            source: 'coupon-top-redeemed',
            form: 'pie',
            metric: 'couponsRedeemed',
            breakdownBy: 'promotion',
            limit: 10,
            size: 'md',
            description:
                "The period's ten most-redeemed coupons as a donut, each slice sized against the other nine rather than against all redemptions: the rest of the catalog holds no slice. Counts the promotions belonging to no campaign and excludes RPP and Road to Rewards. Follows the page Period and Region filters.",
        },
    ],
    'blue'
);
